import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  timeOffRequestsAPI,
  tasksAPI,
  projectsAPI,
  usersAPI,
} from '../services/api';
import UnifiedCalendarView from '../components/manager/UnifiedCalendarView';
import CalendarViewToggle from '../components/calendar/CalendarViewToggle';
import CalendarWeekView from '../components/calendar/CalendarWeekView';
import CalendarDayView from '../components/calendar/CalendarDayView';

type CalendarView = 'month' | 'week' | 'day';

interface User {
  id: number;
  full_name: string;
}

interface UnifiedEntry {
  id: number;
  type: 'time_off' | 'task' | 'project';
  title: string;
  dueDate: string;
  status: string;
  // Optional metadata
  request_type?: string;
  start_date?: string;
  end_date?: string;
  user?: { id?: number; full_name?: string };
  user_id?: number;
  project?: { id: number; name: string };
}

const TIMEOFF_STATUS_OPTIONS = ['pending', 'approved', 'rejected', 'cancelled'];
const TIMEOFF_TYPE_OPTIONS = [
  'vacation',
  'sick_leave',
  'personal',
  'emergency',
];
const TASK_STATUS_OPTIONS = [
  'todo',
  'in_progress',
  'pending',
  'on_hold',
  'done',
];
const PROJECT_STATUS_OPTIONS = ['active', 'on_hold', 'completed', 'archived'];
const ENTRY_TYPE_OPTIONS = ['time_off', 'task', 'project'];

const pad = (n: number) => String(n).padStart(2, '0');
const toIsoDate = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const parseDate = (s?: string | null): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (y && m && d) return new Date(y, m - 1, d);
  return null;
};

const ManagerTimeOffCalendar: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // View state
  const [currentView, setCurrentView] = useState<CalendarView>(() => {
    const v = searchParams.get('view');
    if (v === 'week' || v === 'day' || v === 'month') return v;
    return 'month';
  });

  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const d = parseDate(searchParams.get('date'));
    return d || new Date();
  });

  const calendarYear = selectedDate.getFullYear();
  const calendarMonth = selectedDate.getMonth() + 1;
  const calendarDay = selectedDate.getDate();

  // Data
  const [timeOffData, setTimeOffData] = useState<any[]>([]);
  const [tasksData, setTasksData] = useState<any[]>([]);
  const [projectsData, setProjectsData] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(() => {
    const v = searchParams.get('entryTypes');
    return v ? new Set(v.split(',')) : new Set(ENTRY_TYPE_OPTIONS);
  });

  const [selectedEmployees, setSelectedEmployees] = useState<Set<number>>(
    () => {
      const emp = searchParams.get('employee');
      return emp ? new Set(emp.split(',').map(Number)) : new Set();
    },
  );

  const [selectedTimeOffStatus, setSelectedTimeOffStatus] = useState<
    Set<string>
  >(() => {
    const v = searchParams.get('timeOffStatus');
    return v ? new Set(v.split(',')) : new Set(TIMEOFF_STATUS_OPTIONS);
  });

  const [selectedTimeOffType, setSelectedTimeOffType] = useState<Set<string>>(
    () => {
      const v = searchParams.get('timeOffType');
      return v ? new Set(v.split(',')) : new Set(TIMEOFF_TYPE_OPTIONS);
    },
  );

  const [selectedTaskStatus, setSelectedTaskStatus] = useState<Set<string>>(
    () => {
      const v = searchParams.get('taskStatus');
      return v ? new Set(v.split(',')) : new Set(TASK_STATUS_OPTIONS);
    },
  );

  const [selectedProjectStatus, setSelectedProjectStatus] = useState<
    Set<string>
  >(() => {
    const v = searchParams.get('projectStatus');
    return v ? new Set(v.split(',')) : new Set(PROJECT_STATUS_OPTIONS);
  });

  // Fetch all three data sources in parallel
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [
          calendarResponse,
          tasksResponse,
          projectsResponse,
          usersResponse,
        ] = await Promise.all([
          timeOffRequestsAPI.calendar(calendarYear, calendarMonth, calendarDay),
          tasksAPI.getAll(),
          projectsAPI.getAll(),
          usersAPI.getAll(),
        ]);
        if (cancelled) return;
        setTimeOffData(calendarResponse.data.calendar_entries || []);
        setTasksData(tasksResponse.data.tasks || tasksResponse.data || []);
        setProjectsData(
          projectsResponse.data.projects || projectsResponse.data || [],
        );
        setUsers(usersResponse.data.users || []);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch calendar data:', err);
          setError(t('calendar.loadError') || 'Failed to load calendar');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [calendarYear, calendarMonth, calendarDay, t]);

  // Unified entries
  const allEntries: UnifiedEntry[] = useMemo(() => {
    const timeOffEntries: UnifiedEntry[] = (timeOffData || []).map(
      (e: any) => ({
        id: e.id,
        type: 'time_off',
        title: e.user?.full_name
          ? `${e.user.full_name} — ${e.request_type}`
          : e.request_type || 'Time off',
        dueDate: e.start_date,
        status: e.status,
        request_type: e.request_type,
        start_date: e.start_date,
        end_date: e.end_date,
        user: e.user,
        user_id: e.user?.id,
      }),
    );

    const taskEntries: UnifiedEntry[] = (tasksData || [])
      .filter((task: any) => !!task.due_date)
      .map((task: any) => ({
        id: task.id,
        type: 'task',
        title: task.title,
        dueDate: task.due_date,
        status: task.status,
        project: task.project,
        user_id: task.assigned_to_user_id,
      }));

    const projectEntries: UnifiedEntry[] = (projectsData || [])
      .filter((project: any) => !!project.end_date)
      .map((project: any) => ({
        id: project.id,
        type: 'project',
        title: project.name,
        dueDate: project.end_date,
        status: project.status,
      }));

    return [...timeOffEntries, ...taskEntries, ...projectEntries];
  }, [timeOffData, tasksData, projectsData]);

  // Apply AND logic filters
  const filteredEntries: UnifiedEntry[] = useMemo(() => {
    return allEntries.filter((entry) => {
      if (!selectedTypes.has(entry.type)) return false;

      if (entry.type === 'time_off') {
        if (!selectedTimeOffStatus.has(entry.status)) return false;
        if (entry.request_type && !selectedTimeOffType.has(entry.request_type))
          return false;
        if (selectedEmployees.size > 0) {
          const uid = entry.user_id ?? entry.user?.id;
          if (uid === undefined || !selectedEmployees.has(uid)) return false;
        }
      } else if (entry.type === 'task') {
        if (!selectedTaskStatus.has(entry.status)) return false;
        if (selectedEmployees.size > 0) {
          const uid = entry.user_id;
          if (uid === undefined || !selectedEmployees.has(uid)) return false;
        }
      } else if (entry.type === 'project') {
        if (!selectedProjectStatus.has(entry.status)) return false;
      }
      return true;
    });
  }, [
    allEntries,
    selectedTypes,
    selectedEmployees,
    selectedTimeOffStatus,
    selectedTimeOffType,
    selectedTaskStatus,
    selectedProjectStatus,
  ]);

  // URL sync
  useEffect(() => {
    const params = new URLSearchParams();
    if (currentView !== 'month') params.set('view', currentView);
    if (currentView !== 'month') {
      params.set('date', toIsoDate(selectedDate));
    } else {
      params.set('date', toIsoDate(selectedDate));
    }
    if (selectedTypes.size < ENTRY_TYPE_OPTIONS.length) {
      params.set('entryTypes', Array.from(selectedTypes).join(','));
    }
    if (selectedEmployees.size > 0) {
      params.set('employee', Array.from(selectedEmployees).join(','));
    }
    if (selectedTimeOffStatus.size < TIMEOFF_STATUS_OPTIONS.length) {
      params.set('timeOffStatus', Array.from(selectedTimeOffStatus).join(','));
    }
    if (selectedTimeOffType.size < TIMEOFF_TYPE_OPTIONS.length) {
      params.set('timeOffType', Array.from(selectedTimeOffType).join(','));
    }
    if (selectedTaskStatus.size < TASK_STATUS_OPTIONS.length) {
      params.set('taskStatus', Array.from(selectedTaskStatus).join(','));
    }
    if (selectedProjectStatus.size < PROJECT_STATUS_OPTIONS.length) {
      params.set('projectStatus', Array.from(selectedProjectStatus).join(','));
    }
    setSearchParams(params, { replace: true });
  }, [
    currentView,
    selectedDate,
    selectedTypes,
    selectedEmployees,
    selectedTimeOffStatus,
    selectedTimeOffType,
    selectedTaskStatus,
    selectedProjectStatus,
    setSearchParams,
  ]);

  const handleMonthChange = (y: number, m: number) => {
    setSelectedDate(new Date(y, m - 1, 1));
  };

  const clearFilters = () => {
    setSelectedTypes(new Set(ENTRY_TYPE_OPTIONS));
    setSelectedEmployees(new Set());
    setSelectedTimeOffStatus(new Set(TIMEOFF_STATUS_OPTIONS));
    setSelectedTimeOffType(new Set(TIMEOFF_TYPE_OPTIONS));
    setSelectedTaskStatus(new Set(TASK_STATUS_OPTIONS));
    setSelectedProjectStatus(new Set(PROJECT_STATUS_OPTIONS));
  };

  const cardClass = isDark
    ? 'bg-slate-800 border-slate-700 border'
    : 'bg-white border-slate-200 border';
  const labelClass = isDark
    ? 'block text-xs font-semibold mb-1 text-slate-400 uppercase'
    : 'block text-xs font-semibold mb-1 text-slate-600 uppercase';
  const selectClass = `w-full border rounded p-2 text-sm ${
    isDark
      ? 'bg-slate-700 border-slate-600 text-white'
      : 'bg-white border-slate-300 text-slate-900'
  }`;

  const formatTimeOffType = (type: string) =>
    type === 'sick_leave'
      ? 'Sick'
      : type.charAt(0).toUpperCase() + type.slice(1);
  const formatLabel = (s: string) =>
    s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');

  // Multi-select dropdown handler
  const handleMultiSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    setter: (s: Set<string>) => void,
    fallback: string[],
  ) => {
    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
    setter(new Set(selected.length > 0 ? selected : fallback));
  };

  // Map entries to UnifiedCalendarItem-compatible shape
  const viewEntries = filteredEntries.map((e) => ({
    id: e.id,
    type: e.type,
    title: e.title,
    dueDate: e.dueDate,
    status: e.status,
    request_type: e.request_type,
    start_date: e.start_date,
    end_date: e.end_date,
  }));

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <h1
          className={
            isDark
              ? 'text-white text-2xl font-bold'
              : 'text-slate-900 text-2xl font-bold'
          }
        >
          {t('manager.timeOffCalendarTitle')}
        </h1>
        <CalendarViewToggle
          currentView={currentView}
          onChange={setCurrentView}
        />
      </div>

      {/* Filter bar - inline horizontal layout */}
      <div className={`mb-6 p-3 rounded-lg ${cardClass}`}>
        <div className="flex flex-col lg:flex-row gap-3 lg:items-end flex-wrap">
          {/* Entry Type filter */}
          <div className="flex-1 min-w-[160px] lg:max-w-xs">
            <label className={labelClass}>
              {t('calendar.filterEntryType') || 'Entry Types'}
            </label>
            <select
              value={Array.from(selectedTypes)}
              onChange={(e) =>
                handleMultiSelectChange(e, setSelectedTypes, ENTRY_TYPE_OPTIONS)
              }
              className={selectClass}
            >
              <option value="">All Status</option>

              {ENTRY_TYPE_OPTIONS.map((typ) => (
                <option key={typ} value={typ}>
                  {formatLabel(typ)}
                </option>
              ))}
            </select>
          </div>

          {/* Employee filter */}
          <div className="flex-1 min-w-[160px] lg:max-w-xs">
            <label className={labelClass}>{t('manager.filterEmployee')}</label>
            <select
              value={Array.from(selectedEmployees).join(',')}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedEmployees(
                  val ? new Set(val.split(',').map(Number)) : new Set(),
                );
              }}
              className={selectClass}
            >
              <option value="">{t('manager.allEmployees')}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </select>
          </div>

          {/* Time-off Status filter */}
          <div className="flex-1 min-w-[160px] lg:max-w-xs">
            <label className={labelClass}>{t('manager.filterStatus')}</label>
            <select
              value={Array.from(selectedTimeOffStatus)}
              onChange={(e) =>
                handleMultiSelectChange(
                  e,
                  setSelectedTimeOffStatus,
                  TIMEOFF_STATUS_OPTIONS,
                )
              }
              className={selectClass}
            >
              {TIMEOFF_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {formatLabel(status)}
                </option>
              ))}
            </select>
          </div>

          {/* Time-off Type filter */}
          <div className="flex-1 min-w-[160px] lg:max-w-xs">
            <label className={labelClass}>{t('manager.filterType')}</label>
            <select
              value={Array.from(selectedTimeOffType)}
              onChange={(e) =>
                handleMultiSelectChange(
                  e,
                  setSelectedTimeOffType,
                  TIMEOFF_TYPE_OPTIONS,
                )
              }
              className={selectClass}
            >
              {TIMEOFF_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {formatTimeOffType(type)}
                </option>
              ))}
            </select>
          </div>

          {/* Task Status filter */}
          <div className="flex-1 min-w-[160px] lg:max-w-xs">
            <label className={labelClass}>
              {t('calendar.filterTaskStatus') || 'Task Status'}
            </label>
            <select
              value={Array.from(selectedTaskStatus)}
              onChange={(e) =>
                handleMultiSelectChange(
                  e,
                  setSelectedTaskStatus,
                  TASK_STATUS_OPTIONS,
                )
              }
              className={selectClass}
            >
              {TASK_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {formatLabel(status)}
                </option>
              ))}
            </select>
          </div>

          {/* Project Status filter */}
          <div className="flex-1 min-w-[160px] lg:max-w-xs">
            <label className={labelClass}>
              {t('calendar.filterProjectStatus') || 'Project Status'}
            </label>
            <select
              value={Array.from(selectedProjectStatus)}
              onChange={(e) =>
                handleMultiSelectChange(
                  e,
                  setSelectedProjectStatus,
                  PROJECT_STATUS_OPTIONS,
                )
              }
              className={selectClass}
            >
              {PROJECT_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {formatLabel(status)}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={clearFilters}
            className="btn btn-secondary px-4 h-10"
          >
            {t('manager.filterClear')}
          </button>
        </div>
      </div>

      {/* Calendar view */}
      {loading ? (
        <div className="text-center py-8">
          <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
            Loading...
          </p>
        </div>
      ) : error ? (
        <div className={`text-center py-8 rounded-lg ${cardClass}`}>
          <p className="text-red-500">{error}</p>
        </div>
      ) : currentView === 'month' ? (
        <UnifiedCalendarView
          isDark={isDark}
          cardClass={cardClass}
          arrowLeftIcon={<span>←</span>}
          chevronRightIcon={<span>→</span>}
          entries={viewEntries}
          year={calendarYear}
          month={calendarMonth}
          onMonthChange={handleMonthChange}
        />
      ) : currentView === 'week' ? (
        <CalendarWeekView
          entries={viewEntries}
          selectedDate={selectedDate}
          isDark={isDark}
          cardClass={cardClass}
          onDateChange={setSelectedDate}
        />
      ) : (
        <CalendarDayView
          entries={viewEntries}
          selectedDate={selectedDate}
          isDark={isDark}
          cardClass={cardClass}
          onDateChange={setSelectedDate}
        />
      )}
    </div>
  );
};

export default ManagerTimeOffCalendar;
